import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface ResendTimerContextType {
  timer: number;
  isExpired: boolean;
  resetTimer: () => void;
}

interface ResendTimerProviderProps {
  children: ReactNode;
  initialTimestamp?: number;
}

const ResendTimerContext = createContext<ResendTimerContextType | undefined>({
  timer: 0,
  isExpired: false,
  resetTimer: () => {},
});

const TIMER_DURATION = 30;

const getRemainingSeconds = (timestamp?: number): number => {
  if (!timestamp) return TIMER_DURATION;
  const elapsed = Math.floor((Date.now() - timestamp) / 1000);
  return Math.max(0, TIMER_DURATION - elapsed);
};

export const ResendTimerProvider = ({ children, initialTimestamp }: ResendTimerProviderProps) => {
  const initialRemaining = getRemainingSeconds(initialTimestamp);
  const [timer, setTimer] = useState(initialRemaining);
  const [isExpired, setIsExpired] = useState(initialRemaining === 0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    setTimer(TIMER_DURATION);
    setIsExpired(false);
  }, []);

  useEffect(() => {
    if (!isExpired) {
      intervalRef.current = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setIsExpired(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isExpired]);

  const value = { timer, isExpired, resetTimer };

  return (
    <ResendTimerContext.Provider value={value}>
      {children}
    </ResendTimerContext.Provider>
  );
};

export const useGetResendTimer = () => {
  const context = useContext(ResendTimerContext);
  if (context === undefined) {
    throw new Error('useGetResendTimer must be used within a ResendTimerProvider');
  }
  return context;
};